<script>
// The "+N more" day list — adapted from
// organization/staffCalendar/BulkSelectPanel.vue, but reading straight
// from the `realEstateCalendar` store/transform.js instead of taking a
// flat `assignments` prop, since this calendar's events already carry
// everything (agent, project, layer, source) needed to group and route
// them. Swapped in for the left panel by AgentCalendar.vue, the same way
// BulkSelectPanel replaces LeftPanel.
import { mapGetters } from 'vuex';
import { transformToEvents } from './transform';

// Deletable straight from this list, same restriction as the right-click
// menu on the main grid — a schedule day can't be deleted without
// deleting the whole schedule.
const DELETABLE_SOURCE_TYPES = ['specific_assignment', 'unavailability'];

export default {
  name: 'DayPanel',
  props: {
    date: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapGetters('realEstateCalendar', {
      calendarData: 'getCalendarData',
      filters: 'getFilters',
    }),

    dayEvents() {
      const labels = {
        viewing: this.$t('REAL_ESTATE.CALENDAR.EVENT_VIEWING'),
        key_handover: this.$t('REAL_ESTATE.CALENDAR.EVENT_KEY_HANDOVER'),
        onLeave: this.$t('REAL_ESTATE.CALENDAR.EVENT_ON_LEAVE'),
      };
      return transformToEvents(this.calendarData, this.filters, labels).filter(
        evt => evt.extendedProps.date === this.date
      );
    },

    workingHoursEvents() {
      return this.dayEvents.filter(evt => evt.extendedProps.kind !== 'booking');
    },

    bookingEvents() {
      return this.dayEvents.filter(evt => evt.extendedProps.kind === 'booking');
    },
  },
  methods: {
    isDeletable(evt) {
      return DELETABLE_SOURCE_TYPES.includes(evt.extendedProps.source_type);
    },
    handleSelect(evt) {
      this.$emit('selectEvent', evt.extendedProps);
    },
    handleDelete(evt) {
      this.$emit('deleteEvent', evt.extendedProps);
    },
  },
};
</script>

<template>
  <div
    class="flex-none w-80 max-w-[20rem] h-full p-4 overflow-auto border-r border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
  >
    <div class="flex justify-between items-center mb-2">
      <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
        {{ date }}
      </h3>
      <woot-button
        icon="dismiss"
        variant="clear"
        color-scheme="alert"
        size="sm"
        @click="$emit('close')"
      />
    </div>

    <p v-if="!dayEvents.length" class="text-sm text-slate-400">
      {{ $t('REAL_ESTATE.CALENDAR.DAY_PANEL_EMPTY') }}
    </p>

    <template v-else>
      <div v-if="workingHoursEvents.length" class="mb-4">
        <p
          class="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 mb-2"
        >
          {{ $t('REAL_ESTATE.CALENDAR.DAY_PANEL_HOURS') }}
        </p>
        <ul class="space-y-1">
          <li
            v-for="evt in workingHoursEvents"
            :key="evt.id"
            class="flex items-center justify-between gap-2 rounded px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
            @click="handleSelect(evt)"
          >
            <span
              class="text-sm truncate flex-1 text-slate-700 dark:text-slate-200"
            >
              {{ evt.title }}
            </span>
            <woot-button
              v-if="isDeletable(evt)"
              icon="delete"
              variant="clear"
              size="small"
              color-scheme="alert"
              @click.stop="handleDelete(evt)"
            />
          </li>
        </ul>
      </div>

      <div v-if="bookingEvents.length">
        <p
          class="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 mb-2"
        >
          {{ $t('REAL_ESTATE.CALENDAR.DAY_PANEL_BOOKINGS') }}
        </p>
        <ul class="space-y-1">
          <li
            v-for="evt in bookingEvents"
            :key="evt.id"
            class="rounded px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer text-sm text-slate-700 dark:text-slate-200"
            @click="handleSelect(evt)"
          >
            {{ evt.title }}
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>
